import { Callout, ICalloutProps } from '@fluentui/react';
import {
  calloutContentStylesPopover,
  CommonStyles,
  IconButton,
  iconButtonStylesNoBG,
  Item,
  itemStylesTag,
  TextWithCopy,
  textWithCopyStylesFill,
  textWithCopyStylesPlain,
  useClassNames,
} from '@h2oai/ui-kit';
import { CSSProperties, useRef, useState } from 'react';

import { UserBasicInfo } from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { CommonImageSrc, DetailField } from '../../featurestore/models';
import { detailHeaderStylesDefault, IDetailHeaderClassNames, IDetailHeaderStyles } from './DetailHeader.styles';

const renderFields = (
  classNames: IDetailHeaderClassNames,
  fields: DetailField[],
  refOwners?: React.RefObject<HTMLDivElement>,
  setIsCalloutVisible?: React.Dispatch<React.SetStateAction<boolean>>
) => {
  return fields && fields.length
    ? fields.map(({ label, value, color }) => {
        return (
          <div key={label} className={`${classNames.field} field-title-${label.toLowerCase().replace(' ', '-')}`}>
            <div className={classNames.fieldTitle}>{label}</div>
            <div
              className={classNames.fieldValue}
              title={value}
              ref={label === 'Owners' ? refOwners : undefined}
              style={
                label === 'Flow' ? { maxWidth: '150px', ...(CommonStyles.truncateString as CSSProperties) } : undefined
              }
            >
              {color && value !== '' && value !== undefined ? (
                <Item
                  styles={itemStylesTag}
                  idField="label"
                  labelField="value"
                  titleField="value"
                  styleField="style"
                  data={{ label, value, style: { backgroundColor: color } }}
                />
              ) : value !== '' && value !== undefined ? (
                <>
                  {value}
                  {label === 'Owners' && setIsCalloutVisible && (
                    <IconButton
                      data-test="icon-button-owners"
                      iconName="ContactInfo"
                      style={{ lineHeight: 24, marginLeft: 8 }}
                      onClick={() => setIsCalloutVisible(true)}
                    />
                  )}
                </>
              ) : (
                '-'
              )}
            </div>
          </div>
        );
      })
    : null;
};

export const getOwnerNames = (owners: UserBasicInfo[] = [], maxLength = 3) => {
  const names = [];
  if (
    owners.some((owner, i) => {
      names.push(owner.name);
      return i === maxLength - 1;
    })
  ) {
    const rest = owners.length - maxLength;
    names.push(`+${rest}`);
  }
  return names.join(', ');
};

interface IDetailHeaderProps {
  data: { [key: string]: any };
  fields: DetailField[][];
  collapsed?: boolean;
}

export const DetailHeader = ({ data, fields, collapsed = true }: IDetailHeaderProps) => {
  const refOwners = useRef<HTMLDivElement>(null);
  const [isCalloutVisible, setIsCalloutVisible] = useState(false);
  const calloutProps: ICalloutProps = {
    target: refOwners.current,
    styles: calloutContentStylesPopover,
    onDismiss: () => {
      setIsCalloutVisible(false);
    },
  };
  const classNames = useClassNames<IDetailHeaderStyles, IDetailHeaderClassNames>(
    'DetailHeader',
    detailHeaderStylesDefault,
    undefined,
    undefined,
    collapsed && 'is-collapsed'
  );
  return (
    <div className={classNames.root} data-test="detail-header">
      <div
        className={classNames.thumbnail}
        style={{
          backgroundImage: `url(${CommonImageSrc.logo512})`,
        }}
      ></div>
      <div className={classNames.body}>
        <div className={classNames.fields}>
          <div className={classNames.description} title={data.description}>
            {data.description || 'No Description'}
          </div>
          {renderFields(classNames, fields[1])}
        </div>
        <div className={classNames.fields}>{renderFields(classNames, fields[0], refOwners, setIsCalloutVisible)}</div>
      </div>

      {isCalloutVisible && (
        <Callout {...calloutProps}>
          <div className={classNames.calloutContents} data-test="owners-container">
            <div className={classNames.calloutHeader}>
              <span>Owners</span>
              <IconButton
                styles={iconButtonStylesNoBG}
                iconName="Clear"
                onClick={() => setIsCalloutVisible(false)}
                data-test="owner-list-close-button"
              />
            </div>
            <div className={classNames.owners} data-test="owners-list">
              {data.owners?.map((owner: UserBasicInfo) => (
                <div className={classNames.owner} key={owner.id} data-test="owners-list-item">
                  <div className={classNames.ownerName}>{owner.name}</div>
                  <div>
                    <TextWithCopy text={owner.email} styles={[textWithCopyStylesPlain, textWithCopyStylesFill]} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Callout>
      )}
    </div>
  );
};
